import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Image as ImageIcon, Download, ArrowLeft, Loader2, Zap, Brain, Shield, FileImage, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { removeBackground } from '@imgly/background-removal';

const BackgroundRemoverPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [processedUrl, setProcessedUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('canvas'); // 'ai' or 'canvas'
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('remove-background-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setError(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Reset processed state
      setProcessedUrl(null);
      setProgress(0);
    }
  };

  const handleRemoveBackgroundCanvas = async () => {
    if (!selectedFile) return;
    
    setIsProcessing(true);
    setError(null);
    setProgress(0);

    try {
      const img = new Image();
      img.src = previewUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      setProgress(30);

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      setProgress(50);
      
      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Sample multiple edge points for better background detection
      const samplePoints = [];
      const edgeSize = Math.min(10, Math.floor(canvas.width * 0.05)); // Sample from edges
      
      // Top edge
      for (let x = 0; x < canvas.width; x += Math.floor(canvas.width / 20)) {
        for (let y = 0; y < edgeSize; y++) {
          const idx = (y * canvas.width + x) * 4;
          samplePoints.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
        }
      }
      
      // Bottom edge
      for (let x = 0; x < canvas.width; x += Math.floor(canvas.width / 20)) {
        for (let y = canvas.height - edgeSize; y < canvas.height; y++) {
          const idx = (y * canvas.width + x) * 4;
          samplePoints.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
        }
      }
      
      // Left edge
      for (let y = 0; y < canvas.height; y += Math.floor(canvas.height / 20)) {
        for (let x = 0; x < edgeSize; x++) {
          const idx = (y * canvas.width + x) * 4;
          samplePoints.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
        }
      }
      
      // Right edge
      for (let y = 0; y < canvas.height; y += Math.floor(canvas.height / 20)) {
        for (let x = canvas.width - edgeSize; x < canvas.width; x++) {
          const idx = (y * canvas.width + x) * 4;
          samplePoints.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
        }
      }
      
      // Calculate average background color from samples
      const bgColor = {
        r: Math.round(samplePoints.reduce((sum, p) => sum + p.r, 0) / samplePoints.length),
        g: Math.round(samplePoints.reduce((sum, p) => sum + p.g, 0) / samplePoints.length),
        b: Math.round(samplePoints.reduce((sum, p) => sum + p.b, 0) / samplePoints.length)
      };
      
      setProgress(70);
      
      // Remove background - make similar colors transparent
      const threshold = 50; // Color similarity threshold (adjustable)
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        // Calculate color distance from background
        const distance = Math.sqrt(
          Math.pow(r - bgColor.r, 2) +
          Math.pow(g - bgColor.g, 2) +
          Math.pow(b - bgColor.b, 2)
        );
        
        // If color is similar to background, make it transparent
        // Use gradient transparency for smoother edges
        if (distance < threshold) {
          const alpha = (distance / threshold) * 255;
          data[i + 3] = Math.max(0, alpha); // Gradual transparency
        }
      }
      
      setProgress(90);
      
      // Put modified image data back
      ctx.putImageData(imageData, 0, 0);
      
      // Convert to blob
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        setProcessedUrl(url);
        setProgress(100);
        setIsProcessing(false);
      }, 'image/png');
      
    } catch (error) {
      console.error('Canvas background removal error:', error);
      setError('Fast mode processing failed. Please try AI Mode for better results with complex backgrounds.');
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleRemoveBackgroundAI = async () => {
    if (!selectedFile) return;
    
    setIsProcessing(true);
    setError(null);
    setProgress(0);

    try {
      // Show initial progress
      setProgress(10);
      
      // Convert file to blob if needed
      const imageBlob = selectedFile instanceof Blob ? selectedFile : await fetch(previewUrl).then(r => r.blob());
      
      setProgress(20);

      // Remove background using the AI library with configuration
      const blob = await removeBackground(imageBlob, {
        publicPath: 'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.7.0/dist/resources/',
        debug: false,
        device: 'cpu',
        proxyToWorker: true,
        fetchArgs: {
          mode: 'cors'
        },
        progress: (key, current, total) => {
          if (total > 0) {
            const percentage = Math.round(20 + (current / total) * 70); // 20-90%
            setProgress(Math.min(percentage, 90));
          }
        }
      });

      setProgress(100);

      // Create URL for the processed image
      const url = URL.createObjectURL(blob);
      setProcessedUrl(url);
      setIsProcessing(false);
      
    } catch (error) {
      console.error('AI background removal error:', error);
      
      // More detailed error message
      let errorMsg = 'AI processing failed. ';
      if (error.message?.includes('fetch') || error.message?.includes('network')) {
        errorMsg += 'Network error loading AI model. Please check your connection and try again, or use Fast Mode.';
      } else if (error.message?.includes('memory') || error.message?.includes('size')) {
        errorMsg += 'Image is too large for AI processing. Please try Fast Mode or use a smaller image.';
      } else {
        errorMsg += 'Please try Fast Mode or a different image.';
      }
      
      setError(errorMsg);
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleRemoveBackground = () => {
    if (mode === 'ai') {
      handleRemoveBackgroundAI();
    } else {
      handleRemoveBackgroundCanvas();
    }
  };

  const handleDownload = () => {
    if (!processedUrl) return;
    
    // Extract filename without extension
    const fileName = selectedFile.name;
    const lastDotIndex = fileName.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
    
    const link = document.createElement('a');
    link.href = processedUrl;
    link.download = `${nameWithoutExt}_AllSmallTools_NoBG.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('remove-background-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Remove Image Background Online — Free & Automatic | AllSmallTooLS</title>
        <meta name="description" content="Automatically remove backgrounds from photos in seconds. Free online background remover for JPG and PNG images." />
        <meta name="keywords" content="remove background, background remover, erase photo background, transparent image maker" />
        <link rel="canonical" href="https://allsmalltools.com/remove-background" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to background remover anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your background remover for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <ImageIcon className="h-8 w-8 text-purple-500" />
              <span>Background Remover</span>
            </h1>
            <p className="text-gray-600">Remove image backgrounds - choose your mode</p>
          </div>
        </div>
      </div>

      {/* Mode Selector */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Select Mode:</span>
            <div className="flex space-x-2">
              <Button
                variant={mode === 'canvas' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('canvas')}
                className={mode === 'canvas' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <Zap className="h-4 w-4 mr-2" />
                Fast Mode (1-2s)
              </Button>
              <Button
                variant={mode === 'ai' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('ai')}
                className={mode === 'ai' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                <Brain className="h-4 w-4 mr-2" />
                AI Mode (15-30s)
              </Button>
            </div>
          </div>
          <div className="text-center mt-3">
            <p className="text-xs text-gray-600">
              {mode === 'canvas' 
                ? '⚡ Fast: Best for solid color backgrounds (white, green screen, etc.)'
                : '🧠 AI: High quality removal for complex backgrounds (first use: ~30MB download)'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">{/* Rest of the component remains the same */}
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Image</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="image-upload"
                disabled={isProcessing}
              />
              <label htmlFor="image-upload" className={isProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}>
                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600">
                  Click to select an image or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Supports JPG, PNG, WebP (Max 10MB)
                </p>
              </label>
            </div>

            {previewUrl && (
              <div className="space-y-4">
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {isProcessing && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Processing...</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      {mode === 'ai' ? (
                        <>
                          {progress < 30 && "Loading AI model..."}
                          {progress >= 30 && progress < 70 && "Analyzing image..."}
                          {progress >= 70 && progress < 100 && "Removing background..."}
                          {progress === 100 && "Complete!"}
                        </>
                      ) : (
                        <>
                          {progress < 50 && "Processing image..."}
                          {progress >= 50 && progress < 100 && "Removing background..."}
                          {progress === 100 && "Complete!"}
                        </>
                      )}
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleRemoveBackground}
                  disabled={isProcessing}
                  className={`w-full ${mode === 'ai' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {mode === 'ai' ? 'Processing with AI...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      {mode === 'ai' ? <Brain className="h-4 w-4 mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                      Remove Background ({mode === 'ai' ? 'AI' : 'Fast'})
                    </>
                  )}
                </Button>

                {!isProcessing && mode === 'ai' && (
                  <p className="text-xs text-gray-500 text-center">
                    First use may take 20-30 seconds to download AI model (~30MB)
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent>
            {processedUrl ? (
              <div className="space-y-4">
                <div className="relative">
                  {/* Checkered background to show transparency */}
                  <div 
                    className="rounded-lg p-4"
                    style={{
                      backgroundImage: 'repeating-conic-gradient(#e5e7eb 0% 25%, #f3f4f6 0% 50%)',
                      backgroundPosition: '0 0, 10px 10px',
                      backgroundSize: '20px 20px'
                    }}
                  >
                    <img 
                      src={processedUrl} 
                      alt="Result" 
                      className="max-w-full h-auto mx-auto"
                    />
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-700">
                    <strong>✓ Background Removed!</strong> The image now has a transparent background (PNG format).
                  </p>
                </div>

                <Button
                  onClick={handleDownload}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Image (PNG)
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <ImageIcon className="h-16 w-16 mx-auto mb-4" />
                <p>Processed image will appear here</p>
                <p className="text-sm mt-2">The background will be transparent</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-4">
              <Zap className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-700">
                <strong>Fast Mode:</strong> Perfect for simple backgrounds like white backgrounds, studio shots, product photos on solid colors, green screens. Processes instantly (1-2 seconds). Works offline. Try this first!
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-4">
              <Brain className="h-6 w-6 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-purple-700">
                <strong>AI Mode:</strong> Best for complex scenes with detailed backgrounds, outdoor photos, portraits with busy backgrounds. Uses advanced ML for precise edge detection. First use downloads 30MB model (cached locally). Worth the wait for complex images!
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-4">
              <ImageIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <strong>100% Private:</strong> Both modes process your images entirely in your browser. Nothing is uploaded to any server. Your images never leave your device. All processing is local and secure.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Background Remover Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Background Remover?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Brain className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Two Removal Modes</h3>
              <p className="text-gray-600">
                Choose between Fast Mode for simple backgrounds or AI Mode for complex images. Our background remover adapts to your needs with intelligent processing.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Complete Privacy</h3>
              <p className="text-gray-600">
                Remove background from images locally in your browser. No uploads to servers. Your photos remain private and secure throughout the entire process.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileImage className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Transparent PNG Output</h3>
              <p className="text-gray-600">
                Get professional transparent background images in PNG format. Perfect for product photos, profile pictures, and graphic design projects.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Need to remove image backgrounds for e-commerce, presentations, or social media? Our free background remover makes it simple. Create transparent PNG images with AI precision or fast processing. Try our remove background tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/resize-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Resize Images
              </Link>
              <Link to="/compress-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Compress Images
              </Link>
              <Link to="/convert-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Convert Images
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Remove Background Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Remove Image Background Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Select Removal Mode:</strong> Choose Fast Mode for simple solid backgrounds or AI Mode for complex backgrounds. Fast Mode processes in 1-2 seconds, AI Mode takes 15-30 seconds.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Upload Your Image:</strong> Click to select your photo or drag and drop. Our background remover supports JPG, PNG, and WebP formats up to 10MB.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Remove Background:</strong> Click \"Remove Background\" and wait for processing. Watch the progress bar as your image background is removed automatically.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download PNG Image:</strong> Preview your image with transparent background. Download the result as a PNG file with transparency preserved.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is background removal free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our background remover is completely free. Remove backgrounds from unlimited images without registration, watermarks, or fees. Both Fast and AI modes are free.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Which mode should I use?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Use Fast Mode for simple, solid-color backgrounds (white, green screen, studio photos). Use AI Mode for complex backgrounds (outdoor scenes, detailed patterns, busy backgrounds). Try Fast Mode first!</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Are my images uploaded anywhere?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">No! All background removal happens in your browser. Your images never leave your device. No server uploads means complete privacy and security for your photos.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">What format is the output?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Output is PNG format with transparent background. PNG preserves transparency, making it perfect for use in designs, presentations, and web graphics where you need a transparent background.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default BackgroundRemoverPage;
